import { Trans } from "@lingui/macro";
import { format } from "date-fns";
import { QRCodeSVG } from "qrcode.react";
import { forwardRef, useMemo } from "react";
import { useMedia } from "react-use";

import { HOME_URL } from "config/env";
import { Token } from "domain/tokens";
import { getHomeUrl } from "lib/legacy";
import { calculateDisplayDecimals } from "lib/numbers";
import { formatAmount, formatPercentage, formatUsd } from "lib/numbers";
import { getTokenVisualMultiplier } from "sdk/configs/tokens";

import SpinningLoader from "components/Common/SpinningLoader";

import logoSmall from "img/logo_small.svg";
import abstractEffect from "img/position-share/abstract_effect.svg";
import mascotCheers from "img/position-share/mascot-cheers.svg";
import mascotRocket from "img/position-share/mascot-rocket.svg";
import mascotSad from "img/position-share/mascot-sad.svg";

type Props = {
  entryPrice: bigint | undefined;
  indexToken: Token;
  isLong: boolean;
  leverage: bigint | undefined;
  loading: boolean;
  markPrice: bigint;
  pnlAfterFeesUsd?: bigint;
  pnlAfterFeesPercentage: bigint;
  userAffiliateCode: any;
};

export const PositionShareCard = forwardRef<HTMLDivElement, Props>(
  (
    {
      entryPrice,
      indexToken,
      isLong,
      leverage,
      loading,
      markPrice,
      pnlAfterFeesPercentage,
      pnlAfterFeesUsd,
      userAffiliateCode,
    },
    ref
  ) => {
    const isMobile = useMedia("(max-width: 400px)");
    const { code } = userAffiliateCode;
    const fallbackHomeUrl = getHomeUrl();
    const homeURL = (HOME_URL || fallbackHomeUrl || "").replace(/\/+$/, "");
    const homeUrlDomain = useMemo(() => {
      try {
        return new URL(homeURL).host;
      } catch (error) {
        void error;
        return homeURL.replace(/^https?:\/\//, "").replace(/\/$/, "");
      }
    }, [homeURL]);

    const priceDecimals = calculateDisplayDecimals(markPrice, undefined, indexToken.visualMultiplier);
    const isPositivePnl = pnlAfterFeesPercentage > 0n;
    const isNegativePnl = pnlAfterFeesPercentage < 0n;
    const pnlAfterFeesUsdLabel = useMemo(() => {
      if (typeof pnlAfterFeesUsd !== "bigint") return undefined;

      return formatUsd(pnlAfterFeesUsd, {
        displayDecimals: 2,
        displayPlus: true,
        visualMultiplier: indexToken.visualMultiplier,
      });
    }, [indexToken.visualMultiplier, pnlAfterFeesUsd]);
    const leverageLabel = leverage !== undefined ? `${formatAmount(leverage, 4, 2, true)}x` : undefined;
    const sideLabel = leverageLabel ? `${isLong ? "Long" : "Short"} ${leverageLabel}` : isLong ? "Long" : "Short";
    const marketLabel = `${getTokenVisualMultiplier(indexToken)}${indexToken.symbol}USD Perpetual`;
    const shareLink = code ? `${homeURL}/?ref=${code}` : `${homeURL}`;
    const cardTimestamp = useMemo(() => format(new Date(), "yyyy-M-d HH:mm:ss"), []);
    const mascotSrc = isNegativePnl ? mascotSad : isPositivePnl ? mascotCheers : mascotRocket;
    const qrCodeSize = isMobile ? 56 : 78;
    const entryPriceLabel =
      formatUsd(entryPrice, {
        displayDecimals: priceDecimals,
        visualMultiplier: indexToken.visualMultiplier,
      }) ?? "-";
    const markPriceLabel =
      formatUsd(markPrice, {
        displayDecimals: priceDecimals,
        visualMultiplier: indexToken.visualMultiplier,
      }) ?? "-";

    return (
      <div className="relative">
        <div
          ref={ref}
          className={`position-share position-share-card ${isNegativePnl ? "is-negative" : "is-positive"}`}
        >
          <div className="position-share-card__main">
            <img className="position-share-card__abstract" src={abstractEffect} alt="" aria-hidden draggable={false} />
            <div className="position-share-card__top-row">
              <div className="position-share-card__brand">
                <span className="position-share-card__brand-icon-wrap">
                  <img
                    className="position-share-card__brand-icon"
                    src={logoSmall}
                    alt=""
                    aria-hidden
                    draggable={false}
                  />
                </span>
                <span className="position-share-card__brand-name">Nectarliquid</span>
              </div>
              <span className="position-share-card__timestamp">{cardTimestamp}</span>
            </div>

            <h3 className="position-share-card__market">{marketLabel}</h3>

            <div className={`position-share-card__side-chip ${isLong ? "is-long" : "is-short"}`}>{sideLabel}</div>

            <div className={`position-share-card__pnl ${isNegativePnl ? "is-negative" : "is-positive"}`}>
              {formatPercentage(pnlAfterFeesPercentage, { signed: true })}
            </div>
            {pnlAfterFeesUsdLabel && (
              <div className={`position-share-card__pnl-usd ${isNegativePnl ? "is-negative" : "is-positive"}`}>
                {pnlAfterFeesUsdLabel}
              </div>
            )}

            <div className="position-share-card__prices">
              <span>
                Entry Price - <strong>{entryPriceLabel}</strong>
              </span>
              <span>
                Mark Price - <strong>{markPriceLabel}</strong>
              </span>
            </div>

            <img className="position-share-card__mascot" src={mascotSrc} alt="Nectarliquid mascot" draggable={false} />
          </div>

          <div className="position-share-card__footer">
            <div className="position-share-card__domain">{homeUrlDomain}</div>
            <div className="position-share-card__referral">
              <div className="position-share-card__referral-text">
                <span>Referral code</span>
                <strong>{code || "-"}</strong>
              </div>
              <QRCodeSVG
                size={qrCodeSize}
                value={shareLink}
                bgColor="#ffffff"
                fgColor="#111111"
                includeMargin={false}
              />
            </div>
          </div>
        </div>
        {loading && (
          <div className="image-overlay-wrapper">
            <div className="image-overlay">
              <SpinningLoader />
              <p className="loading-text">
                <Trans>Generating shareable image...</Trans>
              </p>
            </div>
          </div>
        )}
      </div>
    );
  }
);
